import { ForwardedRef, forwardRef } from "react";
import Select, { SelectInstance } from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { useLazyQuery } from "@apollo/client";
import useSelectStyles from "utils/useSelectStyles";
import { useCurrentBrandId } from "utils/currentUserHooks";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import useFetchOptions from "components/Shared/Select/useFetchOptions";

const optionFragment = gql(/* GraphQL */ `
  fragment ManufacturingFacilitySelectOption on ManufacturingFacility {
    id
    name
  }
`);

type Option = DocumentType<typeof optionFragment>;

const valuesQuery = gql(/* GraphQL */ `
  query ManufacturingFacilitySelectValue($ids: [UUID!]!) {
    manufacturingFacilitiesList(filter: { id: { in: $ids } }) {
      ...ManufacturingFacilitySelectOption
    }
  }
`);

const optionsQuery = gql(/* GraphQL */ `
  query ManufacturingFacilitySelectOptions($brandId: UUID!) {
    manufacturingFacilitiesList(
      condition: { brandId: $brandId }
      orderBy: NAME_ASC
    ) {
      ...ManufacturingFacilitySelectOption
    }
  }
`);

function getOptions(data: DocumentType<typeof optionsQuery>) {
  return getFragmentData(optionFragment, data?.manufacturingFacilitiesList);
}

type Props<IsMulti extends boolean = false> = Omit<
  StateManagerProps<Option, IsMulti>,
  "onChange" | "value"
> &
  IdValueSelectProps<IsMulti>;

function ManufacturingFacilitySelect<IsMulti extends boolean = false>(
  { isMulti, onChange, value: rawValue, isLoading, ...rest }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles<Option, IsMulti>();
  const brandId = useCurrentBrandId();

  const idValueProps = useIdValue<Option, IsMulti, typeof valuesQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.manufacturingFacilitiesList),
    isMulti,
    labelKey: "name",
    onChange,
    query: valuesQuery,
    value: rawValue,
  });

  const lazyQuery = useLazyQuery(optionsQuery, {
    fetchPolicy: "cache-and-network",
    variables: brandId ? { brandId } : undefined,
  });

  const fetchOptionsProps = useFetchOptions({
    getOptions,
    lazyQuery,
    menuIsOpen: rest.menuIsOpen,
    skip: !brandId,
  });

  return (
    <Select<Option, IsMulti>
      {...styleProps}
      {...idValueProps}
      {...fetchOptionsProps}
      {...{ isMulti, ref }}
      isLoading={
        idValueProps.isLoading || fetchOptionsProps.isLoading || isLoading
      }
      placeholder="Select a Manufacturing Facility…"
      noOptionsMessage={() => "No Manufacturing Facilities found"}
      {...rest}
    />
  );
}

export default forwardRef(ManufacturingFacilitySelect);
